import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import CrmButton from './CrmButton';
import './crm-ui.scss';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function CrmModal({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEscape = true,
  className = '',
  backdropClassName = '',
}) {
  const titleId = useId();
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;

    const previousActive = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusTimer = requestAnimationFrame(() => {
      const focusTarget = dialogRef.current?.querySelector(FOCUSABLE);
      (focusTarget || dialogRef.current)?.focus();
    });

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && closeOnEscape) {
        event.preventDefault();
        onCloseRef.current?.();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = [...(dialogRef.current?.querySelectorAll(FOCUSABLE) || [])];
      if (!focusable.length) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      cancelAnimationFrame(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previousActive?.focus?.();
    };
  }, [closeOnEscape, open]);

  if (!open) return null;

  return createPortal(
    <div
      className={`crm-modal-backdrop ${backdropClassName}`.trim()}
      onMouseDown={(event) => {
        if (closeOnBackdrop && event.target === event.currentTarget) onClose?.();
      }}
    >
      <section
        ref={dialogRef}
        className={`crm-modal crm-modal--${size} ${className}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        <header className="crm-modal__header">
          <div>
            {title ? <h2 id={titleId} className="crm-modal__title">{title}</h2> : null}
            {subtitle ? <p className="crm-modal__subtitle">{subtitle}</p> : null}
          </div>
          {onClose ? (
            <CrmButton
              iconOnly
              variant="tertiary"
              size="small"
              onClick={onClose}
              aria-label="Close dialog"
            >
              <CloseIcon fontSize="small" />
            </CrmButton>
          ) : null}
        </header>
        <div className="crm-modal__body">{children}</div>
        {footer ? <footer className="crm-modal__footer">{footer}</footer> : null}
      </section>
    </div>,
    document.body
  );
}

export default CrmModal;
